import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Apple, Play, X, Car, UserCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const APP_LINKS = {
  rider: {
    appStore: "https://apps.apple.com/ca/app/tripsi-riders/id6753165525",
    googlePlay: "https://play.google.com/store/apps/details?id=com.tripsi.riders",
  },
  driver: {
    appStore: "https://apps.apple.com/ca/app/tripsi-driver/id6753165630",
    googlePlay: "https://play.google.com/store/apps/details?id=com.tripsi.driver",
  },
};

function trackDownloadClick(appType, store, page = "") {
  fetch(`${API}/analytics/download-click`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ app_type: appType, store, page: page || window.location.pathname }),
  }).catch(() => {});
}

export function trackPageView(page) {
  fetch(`${API}/analytics/page-view`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ page, referrer: document.referrer || "" }),
  }).catch(() => {});
}

export function AppDownloadModal({ open, onClose }) {
  const [role, setRole] = useState(null);

  const handleClose = () => {
    setRole(null);
    onClose();
  };

  const handleRole = (r) => setRole(r);
  const handleBack = () => setRole(null);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          onClick={handleClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#141414] border border-white/10 w-full max-w-md overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              data-testid="download-modal-close"
              className="absolute top-4 right-4 text-[#A3A3A3] hover:text-white transition-colors z-10"
            >
              <X size={20} />
            </button>

            <AnimatePresence mode="wait">
              {!role ? (
                <motion.div
                  key="choose-role"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="p-8"
                >
                  <h3 className="font-['Outfit'] text-xl md:text-2xl font-bold text-white mb-2">
                    Download Tripsi
                  </h3>
                  <p className="text-[#A3A3A3] text-sm mb-8">
                    Which app are you looking for?
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => handleRole("rider")}
                      data-testid="download-modal-rider"
                      className="group bg-[#0A0A0A] border border-white/10 hover:border-[#E60000]/50 p-6 text-center transition-all duration-200 hover:-translate-y-1"
                    >
                      <div className="w-14 h-14 bg-[#E60000]/10 group-hover:bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4 transition-colors">
                        <UserCircle size={28} className="text-[#E60000]" />
                      </div>
                      <p className="font-['Outfit'] text-base font-bold text-white mb-1">I'm a Rider</p>
                      <p className="text-[#A3A3A3] text-xs">Book rides</p>
                    </button>

                    <button
                      onClick={() => handleRole("driver")}
                      data-testid="download-modal-driver"
                      className="group bg-[#0A0A0A] border border-white/10 hover:border-[#E60000]/50 p-6 text-center transition-all duration-200 hover:-translate-y-1"
                    >
                      <div className="w-14 h-14 bg-[#E60000]/10 group-hover:bg-[#E60000]/20 flex items-center justify-center mx-auto mb-4 transition-colors">
                        <Car size={28} className="text-[#E60000]" />
                      </div>
                      <p className="font-['Outfit'] text-base font-bold text-white mb-1">I'm a Driver</p>
                      <p className="text-[#A3A3A3] text-xs">Earn money</p>
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="store-links"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="p-8"
                >
                  <button
                    onClick={handleBack}
                    data-testid="download-modal-back"
                    className="text-[#A3A3A3] hover:text-white text-sm mb-6 transition-colors flex items-center gap-1"
                  >
                    &larr; Back
                  </button>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E60000]/20 flex items-center justify-center">
                      {role === "rider" ? <UserCircle size={20} className="text-[#E60000]" /> : <Car size={20} className="text-[#E60000]" />}
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] text-lg font-bold text-white">
                        Tripsi {role === "rider" ? "Riders" : "Driver"}
                      </h3>
                      <p className="text-[#A3A3A3] text-xs">
                        {role === "rider" ? "Book rides at the lowest fares" : "Keep 100% of your earnings"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <a
                      href={APP_LINKS[role].appStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackDownloadClick(role, "appstore")}
                      data-testid={`download-${role}-appstore`}
                      className="flex items-center gap-4 bg-[#0A0A0A] border border-white/10 hover:border-[#E60000]/50 p-4 transition-all duration-200 group w-full"
                    >
                      <div className="w-12 h-12 bg-white/5 group-hover:bg-white/10 flex items-center justify-center shrink-0 transition-colors">
                        <Apple size={24} className="text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[#A3A3A3] text-xs">Download on the</p>
                        <p className="text-white font-semibold text-base">App Store</p>
                      </div>
                    </a>

                    <a
                      href={APP_LINKS[role].googlePlay}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackDownloadClick(role, "googleplay")}
                      data-testid={`download-${role}-googleplay`}
                      className="flex items-center gap-4 bg-[#0A0A0A] border border-white/10 hover:border-[#E60000]/50 p-4 transition-all duration-200 group w-full"
                    >
                      <div className="w-12 h-12 bg-white/5 group-hover:bg-white/10 flex items-center justify-center shrink-0 transition-colors">
                        <Play size={24} className="text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[#A3A3A3] text-xs">Get it on</p>
                        <p className="text-white font-semibold text-base">Google Play</p>
                      </div>
                    </a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function useDownloadModal() {
  const [open, setOpen] = useState(false);
  return { open, openModal: () => setOpen(true), closeModal: () => setOpen(false) };
}

export function DownloadButton({ variant = "primary", label, icon, className = "", onClick }) {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      data-testid={`download-btn-${label?.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex items-center gap-3 font-semibold text-sm transition-colors duration-200 ${
        variant === "primary"
          ? "bg-[#E60000] hover:bg-[#FF3B30] text-white px-6 py-3.5"
          : variant === "outline"
          ? "bg-white/10 hover:bg-white/15 border border-white/20 text-white px-6 py-3.5"
          : variant === "white"
          ? "bg-white text-[#E60000] hover:bg-white/90 px-6 py-3.5 font-bold"
          : variant === "white-outline"
          ? "bg-transparent border-2 border-white text-white hover:bg-white/10 px-6 py-3.5 font-bold"
          : variant === "small"
          ? "bg-white/10 hover:bg-white/15 border border-white/10 text-white px-5 py-2.5"
          : variant === "small-white"
          ? "bg-white text-[#E60000] px-5 py-2.5 font-bold"
          : variant === "small-white-outline"
          ? "bg-transparent border-2 border-white text-white px-5 py-2.5 font-bold"
          : ""
      } ${className}`}
    >
      {Icon && <Icon size={variant.startsWith("small") ? 16 : 20} />}
      {label}
    </button>
  );
}
